import { loadXML } from "./xmlLoader.js";
import { normalizeLIDO } from "./normalize.js";

/** Base path where your image files are served from (prefix for linkResource). */
const IMAGE_BASE_PATH = "/data/images/";

/**
 * Return the directory part of a path (incl. trailing slash).
 * @param {string} path
 * @returns {string}
 */
function dirname(path) {
  const i = path.lastIndexOf("/");
  return i >= 0 ? path.slice(0, i + 1) : "";
}

/**
 * Wait for the Verovio UMD WASM build to be initialized on `window.verovio`.
 * Resolves once the runtime is ready.
 * @returns {Promise<void>}
 */
function waitForVerovio() {
  return new Promise((resolve) => {
    if (window.verovio?.module?.calledRun) return resolve();

    const mod = window.verovio?.module;
    if (!mod) {
      // Poll until the script has attached module
      const t = setInterval(() => {
        if (window.verovio?.module) {
          clearInterval(t);
          waitForVerovio().then(resolve);
        }
      }, 10);
      return;
    }

    const prev = mod.onRuntimeInitialized;
    mod.onRuntimeInitialized = () => {
      if (typeof prev === "function") prev();
      resolve();
    };
  });
}

/**
 * Render a Plaine & Easie incipit (if present) into a container using Verovio.
 * Keeps the element if no incipit exists (so you can style a placeholder in CSS).
 * @param {HTMLElement|null} container
 * @param {{paeInline?: string}} incipit
 * @returns {Promise<void>}
 */
async function renderIncipit(container, incipit) {
  if (!container) return;
  if (!incipit?.paeInline) return; // silent: no incipit for this record

  await waitForVerovio();

  const tk = new window.verovio.toolkit();
  tk.setOptions({
    scale: 40,
    pageHeight: 100,
    adjustPageHeight: true,
    footer: "none",
  });

  const svg = tk.renderData(incipit.paeInline, { inputFrom: "pae" });
  container.innerHTML = svg;
}

/**
 * Initialize the detail view:
 * - Read `id` from query string
 * - Find the matching LIDO record across all manifest entries
 * - Render title/date, gallery, summary, meta, tags
 * - Render incipit (if present) via Verovio
 * @returns {Promise<void>}
 */
async function initDetail() {
  const container = document.getElementById("detail");
  const tpl = /** @type {HTMLTemplateElement | null} */ (
    document.getElementById("detail-tpl")
  );
  if (!container || !tpl) return;

  const id = new URLSearchParams(location.search).get("id");
  if (!id) {
    container.innerHTML = "<p>No id given.</p>";
    return;
  }

  // Load manifest of XML basenames
  /** @type {string[]} */
  let files = [];
  try {
    const res = await fetch("/data/manifest.json", {
      headers: { Accept: "application/json" },
    });
    if (!res.ok) throw new Error(String(res.status));
    files = await res.json();
  } catch {
    container.innerHTML = "<p>Could not load manifest.</p>";
    return;
  }

  // Find the matching record
  let item = null;
  for (const rel of files) {
    const xmlPath = `/data/${rel}.xml`;
    try {
      const xml = await loadXML(xmlPath);
      const baseHref = `/${dirname(`data/${rel}`)}`;
      const items = normalizeLIDO(xml, { imageBaseHref: baseHref });
      item = items.find((x) => x.id === id);
      if (item) break;
    } catch (err) {
      console.warn("Could not parse", rel, err);
    }
  }

  if (!item) {
    container.innerHTML = `<p>Record with id=${id} not found.</p>`;
    return;
  }

  // --- Render content ---
  const frag = tpl.content.cloneNode(true);

  // Title + date
  const h1 = /** @type {HTMLElement} */ (frag.querySelector(".title"));
  const timeEl = /** @type {HTMLElement} */ (frag.querySelector(".date"));
  if (h1) h1.textContent = item.title || "";
  if (timeEl) timeEl.textContent = item.date || "";

  // Gallery (all images)
  const gal = /** @type {HTMLElement|null} */ (frag.querySelector(".gallery"));
  if (gal) {
    (item.media?.images || []).forEach((im) => {
      const fig = document.createElement("figure");
      const img = document.createElement("img");
      img.src = IMAGE_BASE_PATH + im.src;
      img.alt = im.alt || item.title || "";
      fig.appendChild(img);
      gal.appendChild(fig);
    });
  }

  // Description (already safe HTML from your data source)
  const body = /** @type {HTMLElement|null} */ (frag.querySelector(".body"));
  if (body) body.innerHTML = item.summary || "";

  // Meta definition list
  const dl = /** @type {HTMLElement|null} */ (frag.querySelector(".meta"));
  if (dl) {
    /** @type {Array<[string, string]>} */
    const meta = [
      ["Repository", item.repository],
      ["Location", item.repositoryLocation],
      ["Inventory No.", item.inventoryNumber],
      [
        "External Link",
        item.link
          ? `<a href="${item.link}" target="_blank" rel="noopener">Link</a>`
          : "",
      ],
    ];
    for (const [label, val] of meta) {
      if (!val) continue;
      const dt = document.createElement("dt");
      dt.textContent = label;
      dt.className = "font-bold";
      const dd = document.createElement("dd");
      dd.innerHTML = val;
      dl.appendChild(dt);
      dl.appendChild(dd);
    }

    // Tags
    if (item.tags?.length) {
      const dt = document.createElement("dt");
      dt.textContent = "Schlagworte";
      dt.className = "font-bold";
      const dd = document.createElement("dd");
      dd.innerHTML = item.tags
        .map(
          (t) =>
            `<span class="inline-block rounded px-2 py-0.5 bg-gray-100 mr-1 mb-1">${t}</span>`
        )
        .join("");
      dl.appendChild(dt);
      dl.appendChild(dd);
    }
  }

  container.appendChild(frag);
  document.title = `${item.title} - Exhibit`;

  // Incipit (if present)
  await renderIncipit(document.getElementById("incipit"), item.incipit);
}

// Boot
initDetail();
